import { useTheme } from '@mui/material';
import clsx from 'clsx';
import isNil from 'lodash/isNil';

import { YAxisTickProps } from './types';
import { getThemeColor } from '../../../../utils/theme';
import { useIsChartHovered } from '../../HoverProvider';

import { tokens } from '../../../../theme/generated/tokens';

export const YAxisTick = <V = string | number,>(props: YAxisTickProps<V>) => {
  const theme = useTheme();
  const { hovered } = useIsChartHovered();
  const {
    payload,
    index = 0,
    tickFormatter,
    className,
    x = 0,
    y: _y = 0,
    orientation = 'left',
    tickConfig,
    width,
    height,
  } = props;

  const label = tickFormatter?.(payload?.value, index) || payload?.value;
  if (isNil(label)) {
    return null;
  }

  const paddingX = tickConfig?.paddingX ?? 0;
  const paddingY = tickConfig?.paddingY ?? 0;
  const fontSize = tickConfig?.fontSize ?? 10;
  const backgroundColor = tickConfig?.backgroundColor
    ? getThemeColor(theme, tickConfig.backgroundColor)
    : 'transparent';
  const color = tickConfig?.color
    ? getThemeColor(theme, tickConfig.color)
    : 'currentColor';

  // SVG axis container width and height provided by recharts
  const foWidth = Number(width ?? 0);
  const foHeight = Number(height ?? 0);

  // Magic number, using this and tickMargin = 0 makes the tick labels align with the axis line
  const hOffset = 6;
  const foX = orientation === 'left' ? -hOffset : -(foWidth - hOffset);

  // To make tick appear 5px above the grid line (DS guidelines)
  const y = Number(_y) - 5;

  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject
        x={foX}
        y={-foHeight}
        width={foWidth}
        height={foHeight}
        style={{ pointerEvents: 'none' }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: orientation === 'right' ? 'flex-end' : 'flex-start',
          }}
        >
          <span
            className={clsx(
              'y-axis-tick',
              `y-axis-tick-${orientation}`,
              className
            )}
            data-testid={`y-axis-tick-${index}`}
            style={{
              fontSize,
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 400,
              color,
              backgroundColor,
              borderRadius: tokens.theme.radius.sm,
              padding: `${paddingY}px ${paddingX}px`,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              opacity: hovered ? 0.6 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            {label}
          </span>
        </div>
      </foreignObject>
    </g>
  );
};
